import { Link } from 'react-router-dom';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { Logo } from '../../components/Logo';
import { useLocale, useT } from '../../lib/i18n';
import { useLocalePath } from '../../marketing/PublicRoute';
import { CookieSettingsLink } from '../../components/CookieConsent';
import { LEGAL, LEGAL_DOCS, type LegalKind } from './content';

/** Privacy policy and terms of service, in the interface language. */
export function LegalPage({ kind }: { kind: LegalKind }) {
  const t = useT();
  const locale = useLocale((s) => s.locale);
  const doc = LEGAL_DOCS[kind][locale];
  const lp = useLocalePath();
  return (
    <div className="min-h-full bg-white">
      <header className="mx-auto flex max-w-3xl items-center gap-4 px-6 py-5">
        <Logo to={lp('/')} />
        <div className="flex-1" />
        <LanguageSwitcher />
      </header>
      <main className="mx-auto max-w-3xl px-6 pt-6 pb-20" data-testid="legal-page">
        <h1 className="text-3xl font-bold tracking-tight text-zinc-900">{doc.title}</h1>
        <p className="mt-2 text-sm text-zinc-500">
          {t('legal.updated', {
            date: new Date(LEGAL.updated).toLocaleDateString(locale, { dateStyle: 'long' }),
          })}
        </p>
        <p className="mt-6 leading-relaxed text-zinc-700">{doc.intro}</p>
        {doc.sections.map((s) => (
          <section key={s.h} className="mt-8">
            <h2 className="text-lg font-semibold text-zinc-900">{s.h}</h2>
            {s.p.map((p) => (
              <p key={p} className="mt-2 leading-relaxed text-zinc-700">
                {p}
              </p>
            ))}
          </section>
        ))}
        <div className="mt-12 flex gap-4 border-t border-zinc-200 pt-6 text-sm">
          <Link
            to={lp(kind === 'privacy' ? '/terms' : '/privacy')}
            className="font-medium text-brand-600 hover:underline"
          >
            {kind === 'privacy' ? t('legal.terms') : t('legal.privacy')}
          </Link>
          <CookieSettingsLink className="text-zinc-500 hover:text-zinc-800" />
          <Link to={lp('/')} className="text-zinc-500 hover:text-zinc-800">
            {t('legal.home')}
          </Link>
        </div>
      </main>
    </div>
  );
}
